import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Avatar, Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { PageTitle } from '@/shared/ui/components';

/**
 * Visual chrome for the staff edit page (PROD-765). Renders
 * back-arrow + avatar + name sub-header, per-tab navigation, and the
 * top-right action button slot. Content slot is the active tab's body.
 *
 * Pure presentation — no fetch, no mutation, no form state. The owning
 * `StaffEditPage` passes everything in. This lets the Storybook story render
 * any state combination without context decorators.
 *
 * Tab order mirrors Flutter `staff_base_widget.dart:84-156`:
 *   Always:     Personal Details, Account Details, Practices
 *   Nurse only: Professional Details, Schedule, Macro (inserted between
 *               Account and Practices, and at end respectively)
 */
export type StaffTabId =
  | 'personal'
  | 'account'
  | 'professional'
  | 'schedule'
  | 'practices'
  | 'macro';

interface StaffTabSpec {
  id: StaffTabId;
  label: string;
  breadcrumbLabel: string;
  nurseOnly: boolean;
}

const ALL_TABS: readonly StaffTabSpec[] = [
  { id: 'personal',     label: 'Personal Details',     breadcrumbLabel: 'PERSONAL DETAILS',     nurseOnly: false },
  { id: 'account',      label: 'Account Details',      breadcrumbLabel: 'ACCOUNT DETAILS',      nurseOnly: false },
  { id: 'professional', label: 'Professional Details', breadcrumbLabel: 'PROFESSIONAL DETAILS', nurseOnly: true },
  { id: 'schedule',     label: 'Schedule',             breadcrumbLabel: 'SCHEDULE',             nurseOnly: true },
  { id: 'practices',    label: 'Practices',            breadcrumbLabel: 'PRACTICES',            nurseOnly: false },
  { id: 'macro',        label: 'Macro',                breadcrumbLabel: 'MACRO',                nurseOnly: true },
];

export interface StaffEditShellProps {
  mode: 'create' | 'edit';
  activeTab: StaffTabId;
  onTabChange: (next: StaffTabId) => void;
  isNurse: boolean;
  staffName: string;
  photoUrl: string | null;
  onBack: () => void;
  saveLabel: string;
  onSave: () => void;
  saveDisabled: boolean;
  isLoading: boolean;
  /**
   * Account Settings mode (Flutter `isFromAccountSettings`). When true the
   * page is the logged-in user editing their OWN staff record: hide the
   * back arrow + the "STAFF" list link (`showBack: !isFromAccountSettings`,
   * staff_base_widget.dart:500) and label the breadcrumb ACCOUNT SETTINGS.
   */
  accountMode?: boolean;
  children: ReactNode;
}

export function StaffEditShell({
  mode,
  activeTab,
  onTabChange,
  isNurse,
  staffName,
  photoUrl,
  onBack,
  saveLabel,
  onSave,
  saveDisabled,
  isLoading,
  accountMode = false,
  children,
}: StaffEditShellProps) {
  const tabs = ALL_TABS.filter((t) => !t.nurseOnly || isNurse);

  return (
    <div data-page="staff-settings-page" className="flex flex-col h-full overflow-hidden">
      {/* Sub-header: back arrow + avatar + name + top-right action */}
      <div data-slot="staff-settings-head" className="px-8 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!accountMode && (
            <button
              type="button"
              onClick={onBack}
              className="icon-action"
              aria-label="Back to staff list"
            >
              <ChevronLeft className="size-5" aria-hidden />
            </button>
          )}
          <Avatar
            src={photoUrl}
            name={staffName}
            size="md"
            fallbackIcon="?"
            className="shrink-0"
          />
          <PageTitle size="lg" className="truncate">
            {mode === 'create' ? 'New Staff Member' : staffName}
          </PageTitle>
        </div>
        <Button type="button" onClick={onSave} disabled={saveDisabled}>
          {saveLabel}
        </Button>
      </div>

      {/* Tab navigation */}
      <div data-slot="staff-settings-tabs" className="px-8 border-b border-border-hairline">
        <nav
          className="flex items-center gap-6 overflow-x-auto scrollbar-hide"
          aria-label="Staff edit tabs"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'relative py-3 text-sm font-semibold whitespace-nowrap transition-colors',
                tab.id === activeTab
                  ? 'text-brand-depth'
                  : 'text-text-muted hover:text-text-default',
              )}
              aria-current={tab.id === activeTab ? 'page' : undefined}
            >
              {tab.label}
              {tab.id === activeTab && (
                <span
                  className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand-primary"
                  aria-hidden
                />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab body */}
      <div
        data-slot="staff-settings-body"
        data-tab={activeTab}
        className="flex-1 overflow-auto px-8 py-6 scrollbar-hide bg-surface-background"
      >
        {isLoading ? (
          <BrandLoading size={64} label="Loading staff…" />
        ) : (
          <div className="max-w-160">{children}</div>
        )}
      </div>
    </div>
  );
}

